/**
 * ESCALA FLUIDA — Alfredo OPS (PDV)
 * ==============================================================================
 *
 * Este arquivo é o par do `escala.ts` da Alfredo Central e usa a MESMA fórmula,
 * com um termo a mais — o de altura. A justificativa desse termo está na seção
 * "Por que o PDV também olha a altura", mais abaixo; o resto do raciocínio é
 * idêntico e está repetido aqui de propósito, para quem abrir só este repo.
 *
 * O problema que este arquivo resolve
 * -----------------------------------
 * O PDV foi desenhado num monitor de 1440x900 e todo tamanho foi escrito em px
 * fixo. Medido no gabarito visual (`_gabarito-visual/antes/medidas.md`): fonte
 * raiz, corpo e altura de botão saem IDÊNTICOS em 1366, 1440 e 1920 — nada
 * escala. O efeito está fotografado: em 1366x768 a linha "Total R$ 0,00" fica
 * CORTADA pela borda de baixo, porque as caixas são em % (encolhem com a tela)
 * e o texto é em px (não encolhe). Em 1920 acontece o contrário: o texto de
 * 16px fica pequeno dentro de caixas esticadas.
 *
 * A ideia: um "pixel fluido" (--u)
 * --------------------------------
 * Define-se UMA unidade — `--u`, que vale exatamente 1px em 1440x900 — e todo
 * tamanho passa a ser `calc(var(--u) * N)`, onde N é o MESMO número que já
 * estava no código. Duas consequências:
 *
 *   1. Em 1440x900 o resultado é aritmeticamente idêntico ao de hoje.
 *   2. A migração é mecânica e auditável: `16px` vira `calc(var(--u) * 16)`.
 *      O número nunca muda; muda só a FORMA de expressá-lo.
 *
 * A fórmula
 * ---------
 *   --u: clamp(0.05rem,
 *              min(0.0175rem + 0.05vw, 0.04rem + 0.025vw, 0.00625rem + 0.1vh),
 *              0.075rem)
 *
 * TRÊS retas, e o `min()` fica com a menor — ou seja, com a dimensão mais
 * apertada. As três valem exatamente 1,00px em 1440x900:
 *
 *   · largura, abaixo de 1440   0.0175rem + 0.05vw   → inclinação 0,72
 *     0,28px + 0,05 x 14,40 = 0,28 + 0,72 = 1,00px exato.
 *     Quase proporcional: abaixo de 1440 o objetivo é NÃO ESTOURAR, e encolher
 *     junto com a janela é o que devolve as proporções do desenho original.
 *
 *   · largura, acima de 1440    0.04rem + 0.025vw    → inclinação 0,36
 *     0,64px + 0,025 x 14,40 = 0,64 + 0,36 = 1,00px exato.
 *     Amortecida: crescer proporcionalmente daria 21,3px de corpo em 1920,
 *     tamanho de outdoor. Amortecido dá 17,9px.
 *     (As duas retas de largura se cruzam em 1440; o `min()` faz a primeira
 *      valer abaixo e a segunda valer acima. Um `clamp()` sozinho só sabe uma
 *      reta, e aqui são necessárias duas inclinações.)
 *
 *   · altura                    0.00625rem + 0.1vh   → inclinação 0,9
 *     0,1px + 0,1 x 9,00 = 0,1 + 0,9 = 1,00px exato em 900px de altura.
 *
 *   · piso 0.05rem (0,8x) e teto 0.075rem (1,2x): congelam nos extremos, para
 *     não encolher além do legível nem virar cartaz num 4K.
 *
 * Por que o PDV também olha a altura
 * ----------------------------------
 * A Central rola; o PDV não pode. É um balcão: tudo que a operação precisa
 * — o total, o botão de fechar conta — tem que caber na tela sem rolagem, ou o
 * caixa não fecha a venda. E o aperto do PDV é VERTICAL, não horizontal: de
 * 1440x900 para 1366x768 a largura cai 5,1% mas a altura cai 14,7%. Uma escala
 * que só olhasse a largura encolheria 3,7% onde o layout precisa de 14% — e a
 * linha "Total" continuaria cortada. Com o termo de altura, 1366x768 recebe
 * 0,868x, que é o encolhimento que a altura pede.
 *
 * Efeito colateral desejado: um monitor 1600x900 recebe 1,000x, exatamente o
 * tamanho de 1440x900. Faz sentido — ganhou largura, não ganhou altura, e é a
 * altura que limita o PDV.
 *
 * Medido no Brave headless:
 *
 *   | viewport   | fator | 14px  | 16px  | 20px  | 24px  | 50px  |
 *   |------------|-------|-------|-------|-------|-------|-------|
 *   | 1024x768   | 0,800 | 11,20 | 12,80 | 16,00 | 19,20 | 40,00 |
 *   | 1280x800   | 0,900 | 12,60 | 14,40 | 18,00 | 21,60 | 45,00 |
 *   | 1366x768   | 0,868 | 12,15 | 13,89 | 17,36 | 20,83 | 43,40 |
 *   | 1440x900   | 1,000 | 14,00 | 16,00 | 20,00 | 24,00 | 50,00 |  <- exato
 *   | 1600x900   | 1,000 | 14,00 | 16,00 | 20,00 | 24,00 | 50,00 |
 *   | 1680x1050  | 1,060 | 14,84 | 16,96 | 21,20 | 25,44 | 53,00 |
 *   | 1920x1080  | 1,120 | 15,68 | 17,92 | 22,40 | 26,88 | 56,00 |
 *   | 1080x1920  | 0,820 | 11,48 | 13,12 | 16,40 | 19,68 | 41,00 |  <- monitor girado
 *   | 2560x1440  | 1,200 | 16,80 | 19,20 | 24,00 | 28,80 | 60,00 |
 *
 * Os coeficientes NÃO são arredondados por estética: 0,05vw = 72/1440,
 * 0,025vw = 36/1440 e 0,1vh = 90/900 são divisões exatas em decimal, e as
 * constantes em rem (0,0175 x 16 = 0,28; 0,04 x 16 = 0,64; 0,00625 x 16 = 0,1)
 * também. É isso que garante que a soma na âncora seja 1,000000 e não
 * 0,999997 — a diferença entre "pixel idêntico" e "pixel quase idêntico".
 *
 * Por que rem e não px na fórmula
 * -------------------------------
 * As constantes e os limites estão em `rem` para que quem aumenta a fonte no
 * navegador continue sendo atendido. Com a raiz no padrão de 16px o resultado é
 * numericamente idêntico ao da versão em px.
 *
 * O que NÃO passa por aqui
 * ------------------------
 * Borda de 1px, largura de traço de SVG e as condições dos `@media` continuam
 * fixas. Cor, raio e sombra também não são assunto deste arquivo: aqui só se
 * muda a forma de expressar TAMANHO.
 *
 * ARMADILHA: não mexer no font-size da raiz (html)
 * ------------------------------------------------
 * A fórmula do `--u` é escrita em `rem`, ou seja, ela LÊ o font-size da raiz.
 * Se alguém tornar o `html { font-size: ... }` fluido também, a escala passa a
 * se alimentar de si mesma e o resultado deixa de bater com o gabarito — e o
 * erro composto cresce nas pontas, onde justamente ninguém está olhando. A raiz
 * fica no padrão do navegador; quem escala é o corpo (`body { font-size:
 * var(--fs-base) }` em global.ts) e cada tamanho declarado.
 */

/** Âncora: a janela em que a escala vale exatamente 1 — onde o PDV foi desenhado. */
export const LARGURA_ANCORA = 1440;
export const ALTURA_ANCORA = 900;

/**
 * O pixel fluido. Vale 1px em 1440x900; 0,868px em 1366x768; 1,12px em 1920x1080.
 * Ver o cabeçalho deste arquivo para a dedução dos coeficientes.
 */
export const UNIDADE_FLUIDA =
  "clamp(0.05rem, min(0.0175rem + 0.05vw, 0.04rem + 0.025vw, 0.00625rem + 0.1vh), 0.075rem)";

/**
 * Converte um tamanho que hoje está em px para a escala fluida, preservando o
 * número. `esc(16)` devolve exatamente 16px em 1440x900 e acompanha a janela
 * fora disso. Usar quando o valor não está na escada nomeada abaixo.
 */
export const esc = (px: number): string => `calc(var(--u) * ${px})`;

/**
 * ESCADA TIPOGRÁFICA — os tamanhos que o PDV realmente usa hoje, cada um com um
 * nome. O número é o valor em 1440x900, então o nome documenta o valor.
 * Levantado do censo do código (`font-size` em px), não inventado:
 * 16px aparece 112x, 20px 56x, 24px 22x, 14px 22x, 50px 20x, 18px 20x.
 * A escada é a MESMA da Central de propósito — é um sistema, não dois.
 */
export const TIPOGRAFIA_PX = {
  "3xs": 10,
  "2xs": 11,
  xs: 12,
  sm: 13,
  md: 14,
  base: 16,
  lg: 18,
  xl: 20,
  "2xl": 24,
  "3xl": 30,
  "4xl": 32,
  "5xl": 40,
  "6xl": 48,
  "7xl": 64,
} as const;

/**
 * ESCADA DE ESPAÇAMENTO — os valores de padding/margin/gap que já existem no
 * código, nomeados pelo valor em 1440x900. Censo do PDV: 10px 189x, 4px 112x,
 * 2px 110x, 5px 96x, 20px 75x.
 */
export const ESPACAMENTO_PX = [
  2, 4, 5, 6, 8, 10, 12, 15, 16, 20, 24, 32, 40,
] as const;

type ChaveTipografia = keyof typeof TIPOGRAFIA_PX;

/** `fs.base` → `var(--fs-base)`, para usar dentro dos styled-components. */
export const fs = Object.fromEntries(
  Object.keys(TIPOGRAFIA_PX).map((nome) => [nome, `var(--fs-${nome})`]),
) as Record<ChaveTipografia, string>;

/** `sp[10]` → `var(--sp-10)`. */
export const sp = Object.fromEntries(
  ESPACAMENTO_PX.map((valor) => [valor, `var(--sp-${valor})`]),
) as Record<(typeof ESPACAMENTO_PX)[number], string>;

/**
 * O bloco `:root` com a unidade e as duas escadas. É interpolado dentro do
 * GlobalStyles (`global.ts`) em vez de virar um segundo `createGlobalStyle`:
 * um único estilo global significa que não existe pergunta de ordem de
 * injeção, e a unidade está garantidamente presente em toda tela que renderiza
 * o GlobalStyles — que é o app inteiro.
 */
export const raizEscala = `
:root {
  --u: ${UNIDADE_FLUIDA};

  /* escada tipográfica */
${Object.entries(TIPOGRAFIA_PX)
  .map(([nome, px]) => `  --fs-${nome}: calc(var(--u) * ${px});`)
  .join("\n")}

  /* escada de espaçamento */
${ESPACAMENTO_PX.map((px) => `  --sp-${px}: calc(var(--u) * ${px});`).join("\n")}
}
`;
